/**
 * adminStyles.js
 * Shared inline-style tokens and small components for all admin pages.
 * Import from any admin page:  import { A, AdminPageShell, AdminCard, ... } from './adminStyles';
 */
import React from 'react';

/* ── Design tokens ───────────────────────────────────────── */
export const A = {
    teal:   '#0f5257',
    tealL:  '#e4f0ee',
    tealD:  '#0a3b3f',
    gold:   '#c9a84c',
    goldL:  '#fdf5e0',
    sage:   '#3e7a58',
    sageL:  '#e8f4ec',
    coral:  '#d94f3d',
    coralL: '#fdf0ee',
    mute:   '#b8c8c6',
    gray:   '#f5f6f5',
    ink:    '#1c2120',
    inkMid: '#4a5c5a',
    inkSoft:'#8fa09e',
    border: '#e2e8e6',
    white:  '#ffffff',
    dark:   '#132b1c',   // admin sidebar dark
};

/* ── Shared card ─────────────────────────────────────────── */
export function AdminCard({ children, style = {}, noPad }) {
    return (
        <div style={{
            background: A.white,
            border: `1px solid ${A.border}`,
            borderRadius: 12,
            padding: noPad ? 0 : '18px 20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            overflow: 'hidden',
            ...style,
        }}>
            {children}
        </div>
    );
}

/* ── Card header row ─────────────────────────────────────── */
export function AdminCardHeader({ title, sub, action, badge, badgeColor }) {
    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: sub ? 4 : 14, gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: A.ink }}>{title}</span>
                    {badge != null && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 20, height: 20, background: badgeColor || A.coral, color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '0 6px' }}>{badge}</span>
                    )}
                </div>
                {sub && <p style={{ margin: '2px 0 10px', fontSize: 11, color: A.inkSoft }}>{sub}</p>}
            </div>
            {action && <div style={{ flexShrink: 0 }}>{action}</div>}
        </div>
    );
}

/* ── Page shell with eyebrow + title ────────────────────── */
export function AdminPageShell({ eyebrow, title, description, children, actions }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingBottom: 48 }}>
            <div>
                {eyebrow && (
                    <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 700, color: A.teal, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                        {eyebrow}
                    </p>
                )}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: A.ink, fontFamily: "'Instrument Serif', Georgia, serif", lineHeight: 1.2 }}>
                            {title}
                        </h1>
                        {description && <p style={{ margin: '4px 0 0', fontSize: 12, color: A.inkSoft }}>{description}</p>}
                    </div>
                    {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
                </div>
            </div>
            {children}
        </div>
    );
}

/* ── Teal primary button ─────────────────────────────────── */
export function AdminBtn({ children, onClick, href, secondary, style = {} }) {
    const base = {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
        textDecoration: 'none', cursor: 'pointer',
        transition: 'opacity 0.15s',
        background: secondary ? A.white : A.teal,
        color: secondary ? A.ink : '#fff',
        border: secondary ? `1px solid ${A.border}` : 'none',
        ...style,
    };
    if (href) {
        const { Link } = require('react-router-dom');
        return <Link to={href} style={base}>{children}</Link>;
    }
    return <button type="button" onClick={onClick} style={base}>{children}</button>;
}

/* ── Small stat box ──────────────────────────────────────── */
export function AdminStat({ label, value, sub, alert }) {
    return (
        <div style={{ padding: '14px 16px', background: alert ? A.coralL : A.gray, border: `1px solid ${alert ? '#f2bdb7' : A.border}`, borderRadius: 10 }}>
            <p style={{ margin: '0 0 2px', fontSize: 10, fontWeight: 700, color: alert ? A.coral : A.inkSoft, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</p>
            <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: alert ? A.coral : A.teal, fontFamily: "'Instrument Serif', serif", lineHeight: 1 }}>{value ?? '—'}</p>
            {sub && <p style={{ margin: '4px 0 0', fontSize: 11, color: alert ? '#c0574a' : A.inkSoft }}>{sub}</p>}
        </div>
    );
}

/* ── Horizontal progress bar ─────────────────────────────── */
export function AdminHBar({ value, max = 100, color }) {
    const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
    return (
        <div style={{ background: '#e2e8e6', borderRadius: 3, height: 7, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color || A.teal, borderRadius: 3, transition: 'width 0.9s ease' }} />
        </div>
    );
}

/* ── Dot ─────────────────────────────────────────────────── */
export function AdminDot({ color }) {
    return <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />;
}

/* ── Table wrapper ───────────────────────────────────────── */
export function AdminTable({ headers, rows, emptyMsg = 'No data' }) {
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                    <tr>
                        {headers.map((h, i) => (
                            <th key={i} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: A.inkSoft, textTransform: 'uppercase', letterSpacing: '0.07em', borderBottom: `1px solid ${A.border}`, background: A.gray, whiteSpace: 'nowrap' }}>
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={headers.length} style={{ padding: '24px 12px', textAlign: 'center', color: A.inkSoft, fontSize: 12 }}>{emptyMsg}</td>
                        </tr>
                    ) : (
                        rows.map((row, ri) => (
                            <tr key={ri} style={{ borderBottom: `1px solid ${A.border}` }}
                                onMouseEnter={e => { e.currentTarget.style.background = A.tealL; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                {row.map((cell, ci) => (
                                    <td key={ci} style={{ padding: '9px 12px', color: ci === 0 ? A.ink : A.inkMid, fontWeight: ci === 0 ? 600 : 400 }}>
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

/* ── Badge ───────────────────────────────────────────────── */
export function AdminBadge({ label, color, bg }) {
    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 20,
            fontSize: 10.5, fontWeight: 600,
            background: bg || A.tealL, color: color || A.teal,
        }}>{label}</span>
    );
}
